import React, { useEffect, useState } from 'react';
import { Alert, Modal, Input, Select, Typography, message } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';
import {
    LIBRARY_FAMILIES,
    controlRefPlaceholder,
    parseTags,
    validateCreateDraft,
} from '../../utils/libraryTemplateModel';

const { Text } = Typography;

/**
 * Manual create (POST /admin/library-templates — v1 draft) and edit
 * (PATCH /admin/library-templates/:id — only while draft / in_review; the API 409s the rest,
 * Commandment VII). platform_admin only (server-enforced; the console only mounts for that
 * persona). Human-authored here: an AI-sourced draft comes from the AI draft flow, never from
 * hand-entered "confidence" fields, so none are offered.
 *
 * ISO/IEC 27001:2022 Cl. 7.5.3 / A.5.37 — every item carries a standard + control reference.
 */
const LibraryItemFormModal = ({ open, item, standardOptions, onClose, onSaved }) => {
    const { request } = useApiRequest();
    const editing = !!item;
    const [standardId, setStandardId] = useState(null);
    const [controlRef, setControlRef] = useState('');
    const [title, setTitle] = useState('');
    const [family, setFamily] = useState(null);
    const [tags, setTags] = useState([]);
    const [bodyText, setBodyText] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setStandardId(item?.standardId || null);
        setControlRef(item?.controlRef || '');
        setTitle(item?.title || '');
        setFamily(item?.family || null);
        setTags(parseTags(item?.tagsJson));
        setBodyText(item?.bodyText || '');
        setError(null);
    }, [open, item]);

    const handleOk = async () => {
        if (!editing) {
            const problem = validateCreateDraft({ standardId, controlRef, title });
            if (problem) return setError(problem);
        } else if (!title.trim()) {
            return setError('Title is required.');
        }
        setSaving(true);
        setError(null);
        try {
            let data;
            if (editing) {
                const body = { title: title.trim(), family, tags };
                if (bodyText.trim()) body.bodyText = bodyText;
                ({ data } = await request('PATCH', `/admin/library-templates/${item.id}`, { body }));
            } else {
                const body = { standardId, controlRef: controlRef.trim(), title: title.trim(), family, tags };
                if (bodyText.trim()) body.bodyText = bodyText;
                ({ data } = await request('POST', '/admin/library-templates', { body }));
            }
            message.success(editing ? 'Template saved.' : 'Draft template created.');
            onSaved(data.item);
        } catch (err) {
            setError(err.message || 'Could not save the template.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            width={720}
            title={editing ? `Edit ${item.controlRef} v${item.version}` : 'New library template'}
            okText={editing ? 'Save changes' : 'Create draft'}
            confirmLoading={saving}
            onOk={handleOk}
            onCancel={onClose}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {editing && item?.sourceType === 'ai_generated' && (
                    <Alert
                        type="warning"
                        showIcon
                        message="Editing the text of an AI draft clears its reference-check result"
                        description="The earlier check described the AI's text, not yours, so it is marked out of date and is not rerun. The draft still needs human approval."
                    />
                )}
                {editing && item?.status === 'in_review' && (
                    <Alert type="info" showIcon message="Saving returns this template to Draft - resubmit it for review afterwards." />
                )}
                {!editing && (
                    <>
                        <div>
                            <Text strong>Standard</Text>
                            <Select
                                aria-label="Standard"
                                showSearch
                                optionFilterProp="label"
                                style={{ width: '100%' }}
                                value={standardId}
                                onChange={setStandardId}
                                options={standardOptions}
                                placeholder="Select a standard"
                            />
                        </div>
                        <div>
                            <Text strong>Control reference</Text>
                            <Input aria-label="Control reference" value={controlRef} onChange={(e) => setControlRef(e.target.value)} placeholder={controlRefPlaceholder(standardId)} />
                        </div>
                    </>
                )}
                <div>
                    <Text strong>Title</Text>
                    <Input aria-label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>
                <div>
                    <Text strong>Control family</Text>
                    <Select
                        aria-label="Control family"
                        allowClear
                        style={{ width: '100%' }}
                        value={family}
                        onChange={(v) => setFamily(v || null)}
                        options={LIBRARY_FAMILIES.map((f) => ({ value: f, label: f }))}
                        placeholder="Not applicable"
                    />
                </div>
                <div>
                    <Text strong>Tags</Text>
                    <Select aria-label="Tags" mode="tags" style={{ width: '100%' }} value={tags} onChange={setTags} tokenSeparators={[',']} placeholder="remote-work, BFSI" />
                </div>
                <div>
                    <Text strong>Template body</Text>
                    <Input.TextArea aria-label="Template body" rows={12} value={bodyText} onChange={(e) => setBodyText(e.target.value)} placeholder="The policy / procedure text an Advisor starts a Stage-7 draft from." />
                </div>
                {error && <Text type="danger" role="alert">{error}</Text>}
            </div>
        </Modal>
    );
};

export default LibraryItemFormModal;
